import { Injectable } from '@angular/core';
import { RouterQuery } from '@datorama/akita-ng-router-store';
import { ComponentController, filterNil } from '@lycoris-nubila/ngx-lycoris';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import {
	distinctUntilChanged,
	map,
	pluck,
	shareReplay,
	switchMap,
	tap,
} from 'rxjs/operators';

import { UserApplicationPort } from 'user/port/user-application.port';
import { IUser } from 'user/user';
import { IUserReference } from 'user/user-reference';

import { AppService } from '../../../app.service';
import { CoachProfileState, CoachProfileStore } from './coach-profile.store';

@Injectable()
@UntilDestroy()
export class CoachProfileController extends ComponentController<CoachProfileState> {
	public coach$: Observable<IUser>;
	public title$: Observable<string>;
	public locale$: Observable<string>;
	public sectors$: Observable<string[]>;
	public biography$: Observable<string>;
	public diplomas$: Observable<IUserReference[]>;
	public certificates$: Observable<IUserReference[]>;

	constructor(
		store: CoachProfileStore,
		private _appService: AppService,
		private _routerQuery: RouterQuery,
		private _translateService: TranslateService,
		private _userApplicationPort: UserApplicationPort
	) {
		super(store);

		this._initObservables();
	}

	private _initObservables() {
		this.coach$ = this._routerQuery.selectParams<string>('id').pipe(
			filterNil(),
			switchMap((coachId) => this._userApplicationPort.observeUserById(coachId)),
			map((data) => data?.data),
			distinctUntilChanged((p, c) => p?.id === c?.id),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.title$ = this.coach$.pipe(
			filterNil(),
			pluck('name'),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.sectors$ = this.coach$.pipe(
			map((coach) => coach?.sectors ?? []),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.diplomas$ = this.coach$.pipe(
			map((coach) => (coach?.diplomas ? [...coach.diplomas] : [])),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.certificates$ = this.coach$.pipe(
			map((coach) => (coach?.certificates ? [...coach.certificates] : [])),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.biography$ = this.coach$.pipe(
			filterNil(),
			switchMap((coach) => coach.organization$),
			map((org) => org?.description),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.locale$ = this._appService.currentLang$;
	}
}
